import type {
  ChatDashboardConfig as F0ChatDashboardConfig,
  DashboardCanvasContent as F0DashboardCanvasContent,
} from '@factorialco/f0-react'

import type {
  DashboardCanvasContent,
  DashboardDateNavigationGranularity,
  DashboardFilterDefinition,
  DashboardItem,
  DashboardNavigationFilterDefinition,
  DashboardRuntimeConfig,
} from './components/types'

const DATE_NAVIGATION_GRANULARITIES: ReadonlySet<string> =
  new Set<DashboardDateNavigationGranularity>([
    'day',
    'week',
    'month',
    'quarter',
    'halfyear',
    'year',
    'range',
  ])

export type DashboardRuntimeConfigInput = {
  title: unknown
  description?: unknown
  items: unknown
  fetchSpecs: unknown
  filters?: unknown
  navigationFilters?: unknown
  snapshot?: unknown
  snapshotDate?: unknown
  dateNavigatorColumn?: unknown
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isDashboardItem(value: unknown): value is DashboardItem {
  if (!isRecord(value)) return false
  if (typeof value.id !== 'string' || typeof value.title !== 'string') return false

  switch (value.type) {
    case 'chart':
      return isRecord(value.chart)
    case 'collection':
      return Array.isArray(value.columns)
    case 'metric':
      return true
    default:
      return false
  }
}

function readDashboardItems(value: unknown): DashboardItem[] | undefined {
  if (!Array.isArray(value)) return undefined
  const items: DashboardItem[] = []
  for (const item of value) {
    if (!isDashboardItem(item)) continue
    items.push(item)
  }
  return items
}

function isDashboardFetchSpecs(value: unknown): value is DashboardRuntimeConfig['fetchSpecs'] {
  return isRecord(value)
}

function isDashboardFilterDefinition(value: unknown): value is DashboardFilterDefinition {
  if (!isRecord(value)) return false
  const isSupportedType =
    value.type === 'in' || value.type === 'numericRange' || value.type === 'date'
  return (
    isSupportedType &&
    typeof value.label === 'string' &&
    typeof value.column === 'string' &&
    typeof value.datasetId === 'string'
  )
}

function readDashboardFilters(value: unknown): DashboardRuntimeConfig['filters'] {
  if (value == null) return undefined
  if (!isRecord(value)) return undefined

  const filters: Record<string, DashboardFilterDefinition> = {}
  for (const [key, filter] of Object.entries(value)) {
    if (!isDashboardFilterDefinition(filter)) continue
    filters[key] = filter
  }
  return Object.keys(filters).length > 0 ? filters : undefined
}

function isDashboardNavigationFilterDefinition(
  value: unknown
): value is DashboardNavigationFilterDefinition {
  if (!isRecord(value)) return false
  const granularities = value.granularities
  const defaultGranularity = value.defaultGranularity
  return (
    value.type === 'dateNavigation' &&
    typeof value.label === 'string' &&
    typeof value.column === 'string' &&
    typeof value.datasetId === 'string' &&
    Array.isArray(granularities) &&
    granularities.every(
      (granularity) =>
        typeof granularity === 'string' && DATE_NAVIGATION_GRANULARITIES.has(granularity)
    ) &&
    (defaultGranularity === undefined ||
      (typeof defaultGranularity === 'string' &&
        DATE_NAVIGATION_GRANULARITIES.has(defaultGranularity)))
  )
}

function readDashboardNavigationFilters(
  value: unknown
): DashboardRuntimeConfig['navigationFilters'] {
  if (value == null) return undefined
  if (!isRecord(value)) return undefined

  const navigationFilters: Record<string, DashboardNavigationFilterDefinition> = {}
  for (const [key, filter] of Object.entries(value)) {
    if (!isDashboardNavigationFilterDefinition(filter)) continue
    navigationFilters[key] = filter
  }
  return Object.keys(navigationFilters).length > 0 ? navigationFilters : undefined
}

export function buildDashboardRuntimeConfig(
  input: DashboardRuntimeConfigInput
): DashboardRuntimeConfig | null {
  const items = readDashboardItems(input.items)
  if (typeof input.title !== 'string' || !items) return null
  const fetchSpecs = isDashboardFetchSpecs(input.fetchSpecs) ? input.fetchSpecs : {}

  return {
    title: input.title,
    description: typeof input.description === 'string' ? input.description : undefined,
    items,
    fetchSpecs,
    filters: readDashboardFilters(input.filters),
    navigationFilters: readDashboardNavigationFilters(input.navigationFilters),
    snapshot: typeof input.snapshot === 'boolean' ? input.snapshot : undefined,
    snapshotDate: typeof input.snapshotDate === 'string' ? input.snapshotDate : undefined,
    dateNavigatorColumn:
      typeof input.dateNavigatorColumn === 'string' ? input.dateNavigatorColumn : undefined,
  }
}

export function toF0ChatDashboardConfig(config: DashboardRuntimeConfig): F0ChatDashboardConfig {
  const compatibleConfig: F0ChatDashboardConfig = {
    title: config.title,
    description: config.description,
    items: config.items,
    fetchSpecs: config.fetchSpecs,
  }

  return Object.assign(compatibleConfig, {
    filters: config.filters,
    navigationFilters: config.navigationFilters,
    snapshot: config.snapshot,
    snapshotDate: config.snapshotDate,
    dateNavigatorColumn: config.dateNavigatorColumn,
  })
}

export function toF0DashboardCanvasContent(
  content: DashboardCanvasContent
): F0DashboardCanvasContent {
  return {
    ...content,
    config: toF0ChatDashboardConfig(content.config),
  }
}
